import React from 'react';
import { CheckCircle2, XCircle, FileText, Info } from 'lucide-react';

export const API = `${window.__BACKEND_URL__}/api`;

// Deep link into User Manual → Admin → Payroll sync troubleshooting (codes from backend/routes/payroll_fix_guide.py).
export const fixLink = (code) => `/user-manual?tab=admin&fix=${code}#fix-${code}`;
export const classifyErrorText = (msg = '') => {
  const m = msg.toLowerCase();
  if (m.includes('not found in quickbooks') || m.includes('could not match')) return 'employee_not_found';
  if (m.includes('6000') || m.includes('business validation') || m.includes('6140') || m.includes('6190')) return 'qbo_business_validation';
  if (m.includes('duplicate')) return 'qbo_duplicate';
  if (m.includes('401') || m.includes('authentication') || m.includes('token') || m.includes('unauthorized')) return 'qbo_auth';
  if (m.includes('permission') || m.includes('not enabled') || m.includes('forbidden') || m.includes('403')) return 'qbo_permission';
  return 'qbo_save_error';
};
export const fixCodeForDetail = (d) => ({ unapproved: 'unapproved', skipped: 'skipped_open_shift', already_pushed: 'already_pushed', exempt: 'exempt' }[d.status]
  || (d.status === 'error' ? classifyErrorText(d.error || d.reason || '') : null));
export const HowToFix = ({ code, testId }) => (code ? (
  <a href={fixLink(code)} target="_blank" rel="noreferrer" className="ml-1 underline whitespace-nowrap" style={{ color: '#7DD3FC' }} data-testid={testId || `fix-link-${code}`} title="Open the step-by-step fix in the User Manual">How to fix ↗</a>
) : null);

// Canonical edit-reason taxonomy. Drives the audit dropdown and gets stored on
// the time entry so HR can sort the payroll-adjustments report by reason class.
export const EDIT_REASONS = [
  { value: 'early_arrival',     label: 'Early arrival (worker clocked in before scheduled start)' },
  { value: 'forgot_clock_out',  label: 'Forgot to clock out (replacing run-on shift)' },
  { value: 'scheduled_cap',     label: 'Scheduled cap (worker exceeded approved shift hours)' },
  { value: 'wrong_job',         label: 'Wrong job / project tagged' },
  { value: 'manual_adjustment', label: 'Manual adjustment (other)' },
  { value: 'other',             label: 'Other (use notes)' },
];

// Datetime helpers — convert a UTC ISO string into local <input type="datetime-local">
// values, and back. The local form input renders the wall-clock the admin recognises.
export const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const localInputToIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

export const fmtHours = (h) => (h == null ? '—' : `${Number(h).toFixed(2)}h`);

// After a data refresh rebuilds the (large) table, scroll the given worker's
// row back into view.
export const scrollWorkerIntoView = (workerId) => {
  if (!workerId) return;
  const el = document.querySelector(`[data-testid="pw-worker-row-${workerId}"]`);
  if (el) el.scrollIntoView({ block: 'center' });
};

export const Stat = ({ label, value, accent = '#FFFFFF', testId }) => (
  <div className="ht-card p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider" style={{ color: '#64748B' }}>{label}</div>
    <div className="text-lg font-semibold" style={{ color: accent }}>{value}</div>
  </div>
);

export const Flag = ({ color, label, testId, title }) => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
    style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
    data-testid={testId}
    title={title}
  >
    {label}
  </span>
);

export const StatusPill = ({ status }) => {
  const map = {
    ready:   { icon: CheckCircle2, color: '#00C896', label: 'Ready' },
    pushed:  { icon: CheckCircle2, color: '#00C896', label: 'Pushed' },
    error:   { icon: XCircle,      color: '#EF4444', label: 'Error' },
    skipped: { icon: FileText,     color: '#FFA000', label: 'Skipped' },
    already_pushed: { icon: CheckCircle2, color: '#7BB8E8', label: 'Already pushed' },
    unapproved: { icon: XCircle, color: '#F59E0B', label: 'Not approved' },
    exempt: { icon: Info, color: '#7BB8E8', label: 'Exempt' },
  };
  const m = map[status] || { icon: Info, color: '#64748B', label: status };
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
      style={{ background: `${m.color}22`, color: m.color, border: `1px solid ${m.color}44` }}
      data-testid={`pw-status-${status}`}
    >
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};

export const DetailsTable = ({ details, excludedIds = [] }) => {
  if (!details || details.length === 0) {
    return <p className="text-xs text-slate-500 py-2">No timesheet entries in this window.</p>;
  }
  const isOut = (d) => excludedIds.includes(d.worker_id) && ['ready', 'error'].includes(d.status);
  return (
    <div className="border border-[#1F2D3F] rounded">
      <table className="w-full text-xs">
        <thead className="bg-[#0F1923]">
          <tr className="text-left" style={{ color: '#94A3B8' }}>
            <th className="px-2 py-1.5">Status</th>
            <th className="px-2 py-1.5">Worker</th>
            <th className="px-2 py-1.5">Day</th>
            <th className="px-2 py-1.5">Hours</th>
            <th className="px-2 py-1.5">Description</th>
            <th className="px-2 py-1.5">Notes</th>
          </tr>
        </thead>
        <tbody>
          {details.map((d, i) => (
            <tr key={`${d.worker_id}-${d.day}-${i}`} className={`border-t border-[#1F2D3F] ${isOut(d) ? 'opacity-50' : ''}`} data-testid={`pw-detail-row-${d.worker_id}-${d.day}`}>
              <td className="px-2 py-1.5">
                {isOut(d) ? <StatusPill status="exempt" /> : <StatusPill status={d.status} />}
              </td>
              <td className="px-2 py-1.5 text-white">{d.worker_name}</td>
              <td className="px-2 py-1.5 text-slate-300">{d.day}</td>
              <td className="px-2 py-1.5 text-slate-300">
                {d.hours != null ? `${d.hours}h ${d.minutes || 0}m` : '—'}
                {(d.ot_hours > 0 || d.ot_minutes > 0) && (
                  <span className="ml-1 font-semibold" style={{ color: '#FFA000' }} data-testid={`pw-detail-ot-${d.worker_id}-${d.day}`}>
                    (OT {d.ot_hours || 0}h {d.ot_minutes || 0}m)
                  </span>
                )}
              </td>
              <td className="px-2 py-1.5 text-slate-300 max-w-[260px] truncate" title={d.description}>{d.description || '—'}</td>
              <td className="px-2 py-1.5" style={{ color: d.status === 'error' && !isOut(d) ? '#EF4444' : '#94A3B8' }}>
                {isOut(d) ? 'Left out of this push by the admin.' : (d.error || d.reason || '—')}
                {!isOut(d) && d.status !== 'ready' && d.status !== 'pushed' && <HowToFix code={fixCodeForDetail(d)} testId={`pw-fix-link-${d.worker_id}-${d.day}`} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
